import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { toRefusal } from '@core/helpers/installation-refusal.helper';
import { roleLabel } from '@core/helpers/role-label.helper';
import {
  Installation,
  MeasurementPoint,
  MeasurementRole,
  Preset,
  Profile,
} from '@core/models/installation.model';
import { RoleSelection } from '@core/models/installation-write.model';
import { InstallationProfileService } from '@core/services/installation-profile.service';
import {
  DialogShellComponent,
  FilledButtonComponent,
  SearchInputComponent,
  SpinnerComponent,
  TagComponent,
  TextButtonComponent,
} from '@custom-components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { PlacedRefusal, placeRefusal } from '../../editor-refusal';
import { ROLE_GROUP_ICONS, roleGroupKey } from '../../role-groups';

export interface ProfileDialogData {
  installationOid: string;
  profile: Profile;
  // The installation's active points, which tell which required points are bound already.
  points: MeasurementPoint[];
}

export interface RequiredPoint {
  role: string;
  label: string;
  unit: string;
  bound: boolean;
  // Required by this preset and not by the installation's profile today.
  isNew: boolean;
}

export interface PointGroup {
  key: string;
  icon: string;
  points: RequiredPoint[];
}

export interface PresetOption {
  code: string;
  name: string;
  description: string;
  // Applying it would change nothing.
  current: boolean;
  roles: RoleSelection[];
  required: RequiredPoint[];
  bound: number;
  noLongerRequired: string[];
}

export interface Consequence {
  before: string;
  name: string;
  after: string;
}

// Stands in for the preset name in a translated sentence, so the name can be set in bold apart
// from the text around it in whichever order the language puts them.
const NAME_MARK = '\u0000';

// Closes with the installation as written, or with nothing when cancelled. The answer carries no
// points, so the opener reads the installation again.
@Component({
  selector: 'xemex-profile-dialog',
  imports: [
    DialogShellComponent,
    FilledButtonComponent,
    MatIconModule,
    SearchInputComponent,
    SpinnerComponent,
    TagComponent,
    TextButtonComponent,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './profile-dialog.component.html',
  styleUrl: './profile-dialog.component.scss',
})
export class ProfileDialogComponent {
  readonly data = inject<ProfileDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef =
    inject<MatDialogRef<ProfileDialogComponent, Installation>>(MatDialogRef);
  private readonly service = inject(InstallationProfileService);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly initial: ReadonlyMap<string, boolean> = new Map(
    this.data.profile.roles
      .filter(role => role.state === 'active')
      .map(role => [role.role, role.required]),
  );

  // A calculated point needs no channel, so it counts as bound.
  private readonly boundRoles: ReadonlySet<string> = new Set(
    this.data.points
      .filter(
        point =>
          point.state === 'active' &&
          (point.nature === 'derived' || point.bindingState === 'bound'),
      )
      .map(point => point.role),
  );

  readonly isLoading = signal(true);
  readonly loadFailed = signal(false);
  private readonly catalogue = signal<ReadonlyMap<string, MeasurementRole>>(new Map());
  private readonly presets = signal<Preset[]>([]);
  readonly query = signal('');
  private readonly selectedCode = signal<string | null>(null);
  readonly isSaving = signal(false);
  readonly refusal = signal<PlacedRefusal | null>(null);
  // Read by the opener once the dialog closes: the installation changed underneath.
  readonly stale = computed(() => this.refusal()?.stale ?? false);

  readonly options = computed<PresetOption[]>(() =>
    this.presets().map(preset => this.option(preset)),
  );

  readonly shownOptions = computed<PresetOption[]>(() => {
    const query = this.query().trim().toLowerCase();
    if (!query) return this.options();
    return this.options().filter(
      option =>
        option.name.toLowerCase().includes(query) ||
        option.required.some(point => point.label.toLowerCase().includes(query)),
    );
  });

  readonly selected = computed<PresetOption | null>(() => {
    const options = this.options();
    return (
      options.find(option => option.code === this.selectedCode()) ??
      options.find(option => option.current) ??
      options.find(option => option.code === this.data.profile.presetCode) ??
      options[0] ??
      null
    );
  });

  readonly groups = computed<PointGroup[]>(() => {
    const required = this.selected()?.required ?? [];
    return Object.entries(ROLE_GROUP_ICONS)
      .map(([key, icon]) => ({
        key,
        icon,
        points: required.filter(point => roleGroupKey(point.role) === key),
      }))
      .filter(group => group.points.length > 0);
  });

  readonly consequence = computed<Consequence | null>(() => {
    const selected = this.selected();
    if (!selected) return null;
    const toSetUp = selected.required.length - selected.bound;
    let key = 'installations.profile.dialog.consequenceMany';
    if (selected.current) key = 'installations.profile.dialog.consequenceCurrent';
    else if (toSetUp === 0) key = 'installations.profile.dialog.consequenceNone';
    else if (toSetUp === 1) key = 'installations.profile.dialog.consequenceOne';
    const [before, after = ''] = this.translate
      .instant(key, { name: NAME_MARK, count: toSetUp })
      .split(NAME_MARK);
    return { before, name: selected.name, after };
  });

  readonly canSave = computed(
    () =>
      !!this.selected() &&
      !this.selected()?.current &&
      !this.isLoading() &&
      !this.isSaving() &&
      !this.stale(),
  );

  constructor() {
    this.load();
  }

  load(): void {
    this.isLoading.set(true);
    this.loadFailed.set(false);
    forkJoin([this.service.roles(), this.service.presets()])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([roles, presets]) => {
          this.catalogue.set(new Map(roles.map(role => [role.role, role])));
          this.presets.set(presets);
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.loadFailed.set(true);
        },
      });
  }

  select(code: string): void {
    this.selectedCode.set(code);
    if (!this.stale()) this.refusal.set(null);
  }

  save(): void {
    const selected = this.selected();
    if (!selected || !this.canSave()) return;
    this.isSaving.set(true);
    this.refusal.set(null);
    this.service
      .update(this.data.installationOid, { roles: selected.roles })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: installation => this.dialogRef.close(installation),
        error: (error: unknown) => {
          this.isSaving.set(false);
          const refusal = toRefusal(error);
          const placed = placeRefusal(refusal);
          this.refusal.set(
            refusal.status === 404
              ? { ...placed, messageKey: 'installations.profile.dialog.gone' }
              : placed,
          );
        },
      });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  // The preset's roles, plus every role the installation has that the preset leaves out, kept as
  // optional: the profile write hides the points of a role it no longer lists, and switching
  // presets should leave the installation's points in place.
  private option(preset: Preset): PresetOption {
    const catalogue = this.catalogue();
    const offered = preset.roles.filter(role => {
      const known = catalogue.get(role.role);
      return !!known?.selectable && (!known.deprecated || this.initial.has(role.role));
    });
    const inPreset = new Set(offered.map(role => role.role));
    const roles: RoleSelection[] = [
      ...offered.map(role => ({ role_code: role.role, required: role.required })),
      ...[...this.initial.keys()]
        .filter(role => !inPreset.has(role))
        .map(role => ({ role_code: role, required: false })),
    ];
    const required = offered
      .filter(role => role.required)
      .map(role => ({
        role: role.role,
        label: this.label(role.role),
        unit: catalogue.get(role.role)?.unit ?? '',
        bound: this.boundRoles.has(role.role),
        isNew: this.initial.get(role.role) !== true,
      }));
    const requiredRoles = new Set(required.map(point => point.role));
    return {
      code: preset.preset,
      name: preset.displayName,
      description: this.description(preset),
      current:
        roles.length === this.initial.size &&
        roles.every(role => this.initial.get(role.role_code) === role.required),
      roles,
      required,
      bound: required.filter(point => point.bound).length,
      noLongerRequired: [...this.initial]
        .filter(([role, isRequired]) => isRequired && !requiredRoles.has(role))
        .map(([role]) => this.label(role)),
    };
  }

  // A preset seeded after this portal release has no translation yet; its own text is the fallback.
  private description(preset: Preset): string {
    const key = `installations.profile.presetDescriptions.${preset.preset}`;
    const translated = this.translate.instant(key);
    return translated === key ? preset.description : translated;
  }

  private label(roleCode: string): string {
    const known = this.catalogue().get(roleCode);
    const fallback =
      known?.displayName ??
      this.data.profile.roles.find(role => role.role === roleCode)?.displayName ??
      roleCode;
    return roleLabel(this.translate, roleCode, fallback);
  }
}
